import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Calendar,
  Clock,
  Package,
  Bell,
  Loader2,
  Check,
  X,
  MessageCircle,
  Star
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { ReviewPromptModal } from '../reviews/GoogleReviewButton';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CURE_DURATIONS = [
  { value: 7, label: '1 semaine' },
  { value: 14, label: '2 semaines' },
  { value: 21, label: '3 semaines' },
  { value: 30, label: '1 mois' },
  { value: 45, label: '1 mois et demi' },
  { value: 60, label: '2 mois' },
  { value: 90, label: '3 mois' },
  { value: 120, label: '4 mois' },
  { value: 180, label: '6 mois' },
];

const CureSetupModal = ({ 
  open, 
  onClose, 
  onComplete, // New prop to redirect to dashboard after sale
  saleData,  // { client_name, client_phone, client_email, products, sale_id }
  serviceType = 'skincare', // For review prompt context
  agentName = null,
  language = 'fr' 
}) => {
  const [loading, setLoading] = useState(false);
  const [cureDuration, setCureDuration] = useState('30');
  const [customDuration, setCustomDuration] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);

  // Initialize selected products when modal opens
  React.useEffect(() => {
    if (open && saleData?.products) {
      const allProducts = [
        ...(saleData.products || []),
        ...(saleData.supplements || [])
      ];
      setSelectedProducts(allProducts.map(p => p.item_id || p.id));
    }
  }, [open, saleData]);

  const allProducts = [
    ...(saleData?.products || []),
    ...(saleData?.supplements || [])
  ];

  const toggleProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(allProducts.map(p => p.item_id || p.id));
    }
    setSelectAll(!selectAll);
  };

  const getDurationDays = () => {
    if (cureDuration === 'custom') {
      return parseInt(customDuration) || 30;
    }
    return parseInt(cureDuration);
  };

  const getEndDate = () => {
    const days = getDurationDays();
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + days);
    return endDate.toLocaleDateString('fr-FR');
  };

  const getReminderDate = () => {
    const days = getDurationDays() - 5; // 5 days before
    const reminderDate = new Date();
    reminderDate.setDate(reminderDate.getDate() + days);
    return reminderDate.toLocaleDateString('fr-FR');
  };

  const handleSubmit = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Sélectionnez au moins un produit pour le suivi');
      return;
    }

    // Validate saleData
    if (!saleData?.client_phone && !saleData?.client_name) {
      toast.error('Informations client manquantes');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      if (!token) {
        toast.error('Session expirée, veuillez vous reconnecter');
        return;
      }
      
      // Get selected product details
      const productsForCure = allProducts.filter(p => 
        selectedProducts.includes(p.item_id || p.id)
      ).map(p => ({
        id: p.item_id || p.id,
        name: p.name || 'Produit',
        price: p.price || 0,
        quantity: p.quantity || 1
      }));

      await axios.post(`${API_URL}/cure-followup/create`, {
        client_phone: saleData.client_phone || '',
        client_name: saleData.client_name || 'Client',
        client_email: saleData.client_email || null,
        sale_id: saleData.sale_id || null,
        products: productsForCure,
        cure_duration_days: getDurationDays(),
        notes: saleData.sale_id ? `Cure créée après vente #${saleData.ticket_mediciel || saleData.sale_id}` : 'Cure créée manuellement'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(
        <div>
          <strong>Suivi de cure créé !</strong>
          <br/>
          Rappel prévu le {getReminderDate()}
        </div>
      );
      
      onClose(true); // Close with success
      // Safe navigation - wrap in try/catch to prevent crashes
      try {
        if (onComplete && typeof onComplete === 'function') {
          onComplete();
        }
      } catch (navError) {
        console.error('Navigation error after cure setup:', navError);
      }
    } catch (error) {
      console.error('Error creating cure followup:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du suivi');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onClose(false); // Close without creating
    // Safe navigation
    try {
      if (onComplete && typeof onComplete === 'function') {
        onComplete();
      }
    } catch (navError) {
      console.error('Navigation error after skip:', navError);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-orange-500" />
            {language === 'fr' ? 'Créer un suivi de cure' : 'Create Cure Follow-up'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Client Info */}
          <div className="bg-cyan-50 rounded-lg p-3">
            <p className="text-sm">
              <strong>Client:</strong> {saleData?.client_name}<br/>
              <strong>Téléphone:</strong> {saleData?.client_phone}
            </p>
          </div>

          {/* Cure Duration */}
          <div>
            <Label>{language === 'fr' ? 'Durée de la cure' : 'Cure Duration'}</Label>
            <Select value={cureDuration} onValueChange={setCureDuration}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner la durée" />
              </SelectTrigger>
              <SelectContent>
                {CURE_DURATIONS.map(d => (
                  <SelectItem key={d.value} value={d.value.toString()}>
                    {d.label}
                  </SelectItem>
                ))}
                <SelectItem value="custom">Personnalisé</SelectItem>
              </SelectContent>
            </Select>
            
            {cureDuration === 'custom' && (
              <div className="mt-2 flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="Nombre de jours"
                  value={customDuration}
                  onChange={(e) => setCustomDuration(e.target.value)}
                  className="w-32"
                />
                <span className="text-sm text-gray-500">jours</span>
              </div>
            )}
          </div>

          {/* Dates Preview */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-cyan-600" />
              <span>Fin de cure: <strong>{getEndDate()}</strong></span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Bell className="w-4 h-4 text-orange-500" />
              <span>Rappel prévu: <strong>{getReminderDate()}</strong></span>
              <Badge variant="outline" className="text-xs">5 jours avant</Badge>
            </div>
          </div>

          {/* Products Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>{language === 'fr' ? 'Produits concernés' : 'Products'}</Label>
              <Button variant="ghost" size="sm" onClick={toggleSelectAll}>
                {selectAll ? 'Désélectionner tout' : 'Sélectionner tout'}
              </Button>
            </div>
            <div className="max-h-40 overflow-y-auto border rounded-lg divide-y">
              {allProducts.map((product, index) => {
                const productId = product.item_id || product.id;
                const isSelected = selectedProducts.includes(productId);
                return (
                  <div 
                    key={index}
                    className={`flex items-center gap-3 p-2 cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-cyan-50' : ''}`}
                    onClick={() => toggleProduct(productId)}
                  >
                    <Checkbox checked={isSelected} />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{product.name}</p>
                      <p className="text-xs text-gray-500">
                        {product.price?.toLocaleString('fr-FR')} FCFA × {product.quantity || 1}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {selectedProducts.length} produit(s) sélectionné(s)
            </p>
          </div>
        </div>

        <DialogFooter className="flex gap-2 flex-wrap">
          <Button 
            variant="ghost" 
            onClick={() => {
              onClose(false);
              if (onComplete) onComplete();
            }}
            className="text-gray-500"
          >
            Terminer
          </Button>
          <Button variant="outline" onClick={handleSkip}>
            <X className="w-4 h-4 mr-1" />
            Passer
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || selectedProducts.length === 0}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Bell className="w-4 h-4 mr-2" />
            )}
            Créer le suivi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CureSetupModal;
